import { CategoryLabels, RestResponse } from "@/types";
import { SxProps, Theme } from "@mui/material";

const IMG_URL = process.env.NEXT_PUBLIC_IMG_URL;

export const resolveImageUrl = (path: string): string => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${IMG_URL}/${path}`;
};

export const RestResponseUtil = {
  successful: <T>(data: T): RestResponse<T> => ({
    success: true,
    error: "0000",
    message: "OK",
    data,
  }),
  error: <T>(errorCode: string, message: string): RestResponse<T> => ({
    success: false,
    error: errorCode,
    message,
    data: null,
  }),
};

export const categoryCodes = [
  "btl",
  "pol",
  "ecn",
  "soc",
  "lif",
  "wld",
  "spt",
  "cul",
  "ent",
  "fod",
  "trv",
  "itc",
  "ccy",
];
export const getRandomCategoryCode = (): string => {
  return categoryCodes[Math.floor(Math.random() * categoryCodes.length)];
};

export const categoryVersions = ["v1", "v2", "v3", "v4", "v5", "v6", "v7", "v8", "v9", "v10", "v11", "v12"];

export const getRandomCategoryVersion = (): string => {
  return categoryVersions[Math.floor(Math.random() * categoryVersions.length)];
};

export const getImageUrlCategoryVersion = (fullPath: string): string => {
  return categoryVersions.find((version) => fullPath.includes(version)) ?? "v1";
};

export const getNextCategoryVersion = (categoryVersion: string): string => {
  const index = categoryVersions.indexOf(categoryVersion);
  if (index === -1) {
    throw new Error(`Invalid category version: ${categoryVersion}`);
  }
  const nextIndex = (index + 1) % categoryVersions.length;
  return categoryVersions[nextIndex];
};

export const categories: CategoryLabels[] = [
  {
    code: "",
    en: "All",
    ko: "전체",
    ja: "すべて",
    "zh-cn": "全部",
    "zh-tw": "全部",
    "zh-hk": "全部",
    de: "Alle",
    es: "Todo",
    fr: "Tout",
    pt: "Tudo",
    ru: "Все",
    ar: "الكل",
    hi: "सभी",
    vi: "Tất cả",
    th: "ทั้งหมด",
    id: "Semua",
  },
  {
    code: "btl",
    en: "Battle Zone",
    ko: "배틀존",
    ja: "バトルゾーン",
    "zh-cn": "战斗区",
    "zh-tw": "戰鬥區",
    "zh-hk": "戰鬥區",
    de: "Kampfzone",
    es: "Zona de Batalla",
    fr: "Zone de Combat",
    pt: "Zona de Batalha",
    ru: "Зона битвы",
    ar: "منطقة المعركة",
    hi: "युद्ध क्षेत्र",
    vi: "Khu chiến đấu",
    th: "โซนต่อสู้",
    id: "Zona Pertempuran",
  },
  {
    code: "pol",
    en: "Politics",
    ko: "정치",
    ja: "政治",
    "zh-cn": "政治",
    "zh-tw": "政治",
    "zh-hk": "政治",
    de: "Politik",
    es: "Política",
    fr: "Politique",
    pt: "Política",
    ru: "Политика",
    ar: "السياسة",
    hi: "राजनीति",
    vi: "Chính trị",
    th: "การเมือง",
    id: "Politik",
  },
  {
    code: "ecn",
    en: "Economy",
    ko: "경제",
    ja: "経済",
    "zh-cn": "经济",
    "zh-tw": "經濟",
    "zh-hk": "經濟",
    de: "Wirtschaft",
    es: "Economía",
    fr: "Économie",
    pt: "Economia",
    ru: "Экономика",
    ar: "الاقتصاد",
    hi: "अर्थव्यवस्था",
    vi: "Kinh tế",
    th: "เศรษฐกิจ",
    id: "Ekonomi",
  },

  {
    code: "itc",
    en: "IT",
    ko: "IT",
    ja: "IT",
    "zh-cn": "IT科技",
    "zh-tw": "IT科技",
    "zh-hk": "IT科技",
    de: "IT",
    es: "Tecnología",
    fr: "Informatique",
    pt: "Tecnologia",
    ru: "ИТ",
    ar: "تكنولوجيا المعلومات",
    hi: "आईटी",
    vi: "CNTT",
    th: "ไอที",
    id: "TI",
  },
  {
    code: "soc",
    en: "Society",
    ko: "사회",
    ja: "社会",
    "zh-cn": "社会",
    "zh-tw": "社會",
    "zh-hk": "社會",
    de: "Gesellschaft",
    es: "Sociedad",
    fr: "Société",
    pt: "Sociedade",
    ru: "Общество",
    ar: "المجتمع",
    hi: "समाज",
    vi: "Xã hội",
    th: "สังคม",
    id: "Masyarakat",
  },
  {
    code: "lif",
    en: "Life",
    ko: "생활",
    ja: "生活",
    "zh-cn": "生活",
    "zh-tw": "生活",
    "zh-hk": "生活",
    de: "Leben",
    es: "Vida",
    fr: "Vie",
    pt: "Vida",
    ru: "Жизнь",
    ar: "الحياة",
    hi: "जीवन",
    vi: "Cuộc sống",
    th: "ชีวิต",
    id: "Kehidupan",
  },
  {
    code: "wld",
    en: "World",
    ko: "세계",
    ja: "世界",
    "zh-cn": "世界",
    "zh-tw": "世界",
    "zh-hk": "世界",
    de: "Welt",
    es: "Mundo",
    fr: "Monde",
    pt: "Mundo",
    ru: "Мир",
    ar: "العالم",
    hi: "विश्व",
    vi: "Thế giới",
    th: "โลก",
    id: "Dunia",
  },
  {
    code: "spt",
    en: "Sports",
    ko: "스포츠",
    ja: "スポーツ",
    "zh-cn": "体育",
    "zh-tw": "體育",
    "zh-hk": "體育",
    de: "Sport",
    es: "Deportes",
    fr: "Sports",
    pt: "Esportes",
    ru: "Спорт",
    ar: "الرياضة",
    hi: "खेल",
    vi: "Thể thao",
    th: "กีฬา",
    id: "Olahraga",
  },
  {
    code: "cul",
    en: "Culture",
    ko: "문화",
    ja: "文化",
    "zh-cn": "文化",
    "zh-tw": "文化",
    "zh-hk": "文化",
    de: "Kultur",
    es: "Cultura",
    fr: "Culture",
    pt: "Cultura",
    ru: "Культура",
    ar: "الثقافة",
    hi: "संस्कृति",
    vi: "Văn hóa",
    th: "วัฒนธรรม",
    id: "Budaya",
  },
  {
    code: "ent",
    en: "Entertainment",
    ko: "엔터",
    ja: "エンターテインメント",
    "zh-cn": "娱乐",
    "zh-tw": "娛樂",
    "zh-hk": "娛樂",
    de: "Unterhaltung",
    es: "Entretenimiento",
    fr: "Divertissement",
    pt: "Entretenimento",
    ru: "Развлечения",
    ar: "الترفيه",
    hi: "मनोरंजन",
    vi: "Giải trí",
    th: "ความบันเทิง",
    id: "Hiburan",
  },
  {
    code: "ccy",
    en: "Crypto",
    ko: "코인",
    ja: "暗号通貨",
    "zh-cn": "加密货币",
    "zh-tw": "加密貨幣",
    "zh-hk": "加密貨幣",
    de: "Krypto",
    es: "Cripto",
    fr: "Crypto",
    pt: "Cripto",
    ru: "Криптовалюта",
    ar: "العملات الرقمية",
    hi: "क्रिप्टो",
    vi: "Tiền mã hóa",
    th: "คริปโต",
    id: "Kripto",
  },
  {
    code: "fod",
    en: "Food",
    ko: "음식",
    ja: "食べ物",
    "zh-cn": "食物",
    "zh-tw": "食物",
    "zh-hk": "食物",
    de: "Essen",
    es: "Comida",
    fr: "Nourriture",
    pt: "Comida",
    ru: "Еда",
    ar: "الطعام",
    hi: "भोजन",
    vi: "Thức ăn",
    th: "อาหาร",
    id: "Makanan",
  },
  {
    code: "trv",
    en: "Travel",
    ko: "여행",
    ja: "旅行",
    "zh-cn": "旅行",
    "zh-tw": "旅行",
    "zh-hk": "旅行",
    de: "Reisen",
    es: "Viaje",
    fr: "Voyage",
    pt: "Viagem",
    ru: "Путешествия",
    ar: "السفر",
    hi: "यात्रा",
    vi: "Du lịch",
    th: "ท่องเที่ยว",
    id: "Perjalanan",
  },
];

export const getRgbaBackground = (opacity: number, backgroundColor?: string) => {
  if (!backgroundColor) {
    return "transparent";
  }
  const clampedOpacity = Math.min(Math.max(opacity, 0), 1);
  const rgbaBackground = `${backgroundColor}${Math.round(clampedOpacity * 255)
    .toString(16)
    .padStart(2, "0")}`;

  return rgbaBackground;
};

export const getCalculatedHeight = (fontSize: number | string): string | number => {
  if (typeof fontSize === "number") {
    return `${fontSize + 12}px`;
  }
  return `calc(${fontSize} + 1rem)`;
};

export const lightTextColors = ["#FFF5CC", "#B0C4DE", "#F8F1E9", "#FFD700", "#E6E6FA", "#D4F4A2", "#FFFFFF", "#8B0000"];

export const getCommonTextStyles = (
  color?: string,
  textAlign?: string,
  fontSize?: string | number,
  backgroundColor?: string
): SxProps<Theme> => {
  let calculatedFontSize: string;

  if (typeof fontSize === "number") {
    calculatedFontSize = `${fontSize}px`;
  } else if (typeof fontSize === "string") {
    const parsed = parseInt(fontSize, 10);
    calculatedFontSize = isNaN(parsed) ? "26px" : `${parsed}px`;
  } else {
    calculatedFontSize = "26px";
  }

  return {
    textAlign: textAlign ?? "center",
    fontWeight: 700,
    color: color || "#FFFFFF",
    fontSize: calculatedFontSize,
    textShadow: lightTextColors.includes(color || "")
      ? "1px 1px 4px rgba(0, 0, 0, 1)"
      : "1px 1px 4px rgba(255, 255, 255, 1)",
    lineHeight: "normal",
    backgroundColor: getRgbaBackground(0.15, backgroundColor),
  };
};
